import { Component, DestroyRef, effect, inject, model, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AMOUNT_DECIMALS,
  AMOUNT_FORMAT,
  CapitalizePipe,
  ClientInvoiceItem,
  ClientInvoiceItemService,
  conditionStringValidator,
  errorToast,
  FormErrorService,
  PercentagePipe,
  successSaveToast,
  ValidationDirective,
} from 'shared';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import { catchError, concatMap, debounceTime, finalize, Observable, of, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { NumericTextBoxComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'rc-client-invoice-item',
  imports: [
    NumberPipe,
    ReactiveFormsModule,
    TranslateModule,
    NumericTextBoxComponent,
    TextBoxComponent,
    ValidationDirective,
    PercentagePipe,
  ],
  providers: [FormErrorService, TranslatePipe, CapitalizePipe],
  templateUrl: './client-invoice-item.component.html',
  styleUrl: './client-invoice-item.component.scss',
  standalone: true,
})
export class ClientInvoiceItemComponent {
  #clientInvoiceItemService = inject(ClientInvoiceItemService);
  #formErrorService = inject(FormErrorService);
  #translateService = inject(TranslateService);
  #notificationService = inject(NotificationService);
  #destroyRef = inject(DestroyRef);

  clientInvoiceItem = model.required<ClientInvoiceItem>();
  readonly = model(false);

  update = output<boolean>();

  clientInvoiceItemForm: FormGroup<ClientInvoiceItemForm> = this.#initForm();

  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits: 2,
    useGrouping: true,
    minimumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;
  protected readonly amountDecimals = AMOUNT_DECIMALS;

  readonly #EDITABLE_FIELDS: (keyof ClientInvoiceItemForm)[] = [
    'amount',
    'retailPrice',
    'taxPrice',
    'conditionString',
    'purchasePrice',
  ];

  #updateQueue$ = new Subject<string>();
  #blurEvent$ = new Subject<void>();
  #latestResult: ClientInvoiceItem | null = null;
  #pendingCount = 0;
  #shouldNotify = false;

  constructor() {
    effect(() => {
      const item = this.clientInvoiceItem();
      if (!item) return;
      for (const field of this.#EDITABLE_FIELDS) {
        const ctrl = this.clientInvoiceItemForm.controls[field];
        if (!ctrl.dirty) ctrl.setValue(item[field as keyof ClientInvoiceItem] as never, { emitEvent: false });
      }
    });

    this.#updateQueue$
      .pipe(
        concatMap((field) =>
          this.#sendFieldUpdate(field).pipe(
            catchError((err: HttpErrorResponse) => {
              const notification = this.#notificationService.show(errorToast);
              if (notification.content) notification.content.instance.setCurrentValue(err);
              this.#formErrorService.setErrorViolations(err);
              return of(null);
            }),
            tap((result) => {
              if (result) this.#latestResult = result;
            }),
            finalize(() => {
              this.#pendingCount--;
              if (this.#shouldNotify) this.#tryPatch();
            }),
          ),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    this.#blurEvent$.pipe(debounceTime(5000), takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#shouldNotify = true;
      this.#tryPatch();
    });
  }

  onFieldBlur(field: keyof ClientInvoiceItemForm) {
    if (!this.#EDITABLE_FIELDS.includes(field)) return;
    const ctrl = this.clientInvoiceItemForm.controls[field];
    if (ctrl.dirty && !ctrl.errors) {
      this.#pendingCount++;
      this.#updateQueue$.next(field as string);
    }
    this.#blurEvent$.next();
  }

  #tryPatch() {
    if (this.#pendingCount > 0) return;
    if (this.clientInvoiceItemForm.dirty) return;
    const result = this.#latestResult;
    if (!result) return;
    this.#latestResult = null;
    this.#shouldNotify = false;
    for (const field of this.#EDITABLE_FIELDS) {
      const ctrl = this.clientInvoiceItemForm.controls[field];
      if (!ctrl.dirty) ctrl.setValue(result[field as keyof ClientInvoiceItem] as never, { emitEvent: false });
    }
    this.clientInvoiceItem.set(result);
    this.update.emit(true);
    const notification = this.#notificationService.show(successSaveToast);
    if (notification.content)
      notification.content.instance.value = this.#translateService.instant('CLIENT_INVOICE_ITEM');
  }

  #sendFieldUpdate(field: string): Observable<ClientInvoiceItem> {
    const ctrl = this.clientInvoiceItemForm.controls[field as keyof ClientInvoiceItemForm];
    if (ctrl.errors) return of(this.clientInvoiceItem());

    const updateRecord: Record<string, string | number | null | boolean | undefined> = {};
    const sentValue = ctrl.value;
    updateRecord[field] = sentValue;

    return this.#clientInvoiceItemService
      .update(this.clientInvoiceItem()['@id']!, updateRecord as unknown as ClientInvoiceItem)
      .pipe(
        tap(() => {
          if (ctrl.value === sentValue) ctrl.markAsPristine();
        }),
      );
  }

  #initForm(): FormGroup<ClientInvoiceItemForm> {
    const form = new FormGroup<ClientInvoiceItemForm>({
      amount: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      retailPrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      taxPrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      conditionString: new FormControl('0', {
        nonNullable: true,
        validators: [conditionStringValidator],
      }),
      purchasePrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }
}

interface ClientInvoiceItemForm {
  amount: FormControl<number>;
  retailPrice: FormControl<number>;
  taxPrice: FormControl<number>;
  conditionString: FormControl<string>;
  purchasePrice: FormControl<number>;
}
